import { pattern as commonPattern } from "../utils/common";
import { phoneDigits } from "utils/phone";

/**
 * Form-level validation for react-hook-form, mirroring how the app defined
 * validators with react-reactive-form's FormBuilder:
 *
 *   const form = useAppForm({
 *     defaultValues: { email: "" },
 *     rules: { email: formRules("email", true) },
 *   });
 *
 * A rule set is a plain object:
 *   { required, pattern, minLength, maxLength, min, max,
 *     validate: { key: (value, values, path) => boolean } }
 * Error types use the same keys as the old validators (`required`, `pattern`,
 * `minLength`, `maxLength`, `min`, `max`, or the custom `validate` key), so the
 * existing `errorMsg={[{ validation: "pattern", ... }]}` config keeps working.
 *
 * Paths may use `*` for array items, e.g. "other.*.first_name", and a rule set
 * may be a function `(values, path) => rules` when it depends on other fields.
 * Rules on an array path (e.g. "floors") validate the whole array; their
 * error is reported at "floors.root".
 */

// Validators.pattern(string) anchors the string with ^...$; a RegExp is used as-is.
const toRegExp = (p) =>
  p instanceof RegExp
    ? p
    : new RegExp(`^${String(p).replace(/^\^/, "").replace(/\$$/, "")}$`);

// Same emptiness check as react-reactive-form's Validators.required
// Spaces only count as empty too
const isEmpty = (v) =>
  v === null || v === undefined || (typeof v === "string" ? v.trim() : v).length === 0;

export const rhfRules = {
  required: { required: true },
  pattern: (p) => ({ pattern: toRegExp(p) }),
  minLength: (n) => ({ minLength: n }),
  maxLength: (n) => ({ maxLength: n }),
  // Validators.min / Validators.max: numeric compare, empty values pass
  min: (n) => ({ min: n }),
  max: (n) => ({ max: n }),
  // Phone digits for the country code next to the field (same group):
  // 9 for UA (+380), 10 for the others. Reported as `minLength`, like before.
  phoneLength: {
    validate: {
      minLength: (value, values, path = "phone_number") => {
        if (isEmpty(value)) return true;
        const codePath = path.split(".").slice(0, -1).concat("countryCode").join(".");
        return String(value).length === phoneDigits(getPath(values, codePath));
      },
    },
  },
};

/** Merge several rule sets, combining their `validate` maps. */
export const combineRules = (...list) =>
  list.filter(Boolean).reduce(
    (acc, r) => ({
      ...acc,
      ...r,
      validate: { ...(acc.validate || {}), ...(r.validate || {}) },
    }),
    {},
  );

/** Same presets as `formControl(status, required)` in components/Form/utils. */
export const formRules = (status, required = false) => {
  const base = required ? rhfRules.required : null;
  switch (status) {
    case "email":
      return combineRules(base, rhfRules.pattern(commonPattern.email));
    case "zip_code":
      return combineRules(
        base,
        rhfRules.minLength(5),
        rhfRules.maxLength(10),
        rhfRules.pattern(commonPattern.zipCode),
      );
    case "name":
      return combineRules(base, rhfRules.maxLength(50));
    case "districtName":
      return combineRules(base, rhfRules.minLength(3), rhfRules.maxLength(50));
    case "phone_number":
      return combineRules(base, rhfRules.phoneLength);
    case "otp":
      return combineRules(base, rhfRules.maxLength(1));
    default:
      return combineRules(base);
  }
};

export const getPath = (obj, path) =>
  !path ? obj : path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);

// "other.*.first_name" -> ["other.0.first_name", "other.1.first_name", ...]
const expandPath = (path, values) => {
  const i = path.indexOf("*");
  if (i < 0) return [path];
  const head = path.slice(0, i).replace(/\.$/, "");
  const tail = path.slice(i + 1);
  const arr = getPath(values, head);
  if (!Array.isArray(arr)) return [];
  return arr.flatMap((_, idx) => expandPath(`${head}.${idx}${tail}`, values));
};

/** Returns { type: true, ... } for every failing rule, or null when valid. */
export const runRules = (rules = {}, value, values, path) => {
  const types = {};
  if (rules.required && isEmpty(value)) types.required = true;
  // Like react-reactive-form, length/pattern/min/max checks skip empty values
  if (!isEmpty(value)) {
    if (rules.minLength != null && value.length < rules.minLength)
      types.minLength = true;
    if (rules.maxLength != null && value.length > rules.maxLength)
      types.maxLength = true;
    if (rules.pattern && !toRegExp(rules.pattern).test(String(value)))
      types.pattern = true;
    const num = parseFloat(value);
    if (rules.min != null && !isNaN(num) && num < rules.min) types.min = true;
    if (rules.max != null && !isNaN(num) && num > rules.max) types.max = true;
  }
  Object.entries(rules.validate || {}).forEach(([key, fn]) => {
    if (!fn(value, values, path)) types[key] = true;
  });
  return Object.keys(types).length ? types : null;
};

/**
 * Run a `{ pathPattern: rules }` map; returns react-hook-form style errors.
 * `isSkipped(path)` lets callers leave out fields (e.g. disabled ones).
 */
export const validateValues = (rulesMap, values, isSkipped = () => false) => {
  const errors = {};
  Object.entries(rulesMap).forEach(([pattern, r]) => {
    expandPath(pattern, values).forEach((path) => {
      if (isSkipped(path)) return;
      const rules = typeof r === "function" ? r(values, path) : r;
      const types = runRules(rules, getPath(values, path), values, path);
      if (!types) return;
      const type = Object.keys(types)[0];
      // Errors for a whole array (e.g. "floors") go to "floors.root",
      // react-hook-form's place for field-array errors
      const keys = path.split(".");
      if (Array.isArray(getPath(values, path))) keys.push("root");
      let target = errors;
      keys.slice(0, -1).forEach((k) => (target = target[k] = target[k] || {}));
      target[keys[keys.length - 1]] = { type, message: type, types };
    });
  });
  return errors;
};

/** react-hook-form resolver built from a `{ pathPattern: rules }` map. */
export const createRulesResolver = (rulesMap) => (values) => {
  const errors = validateValues(rulesMap, values);
  return { values: Object.keys(errors).length ? {} : values, errors };
};
